import { formatMoney, formatPercent, formatNumber } from '@lib/utils';
import {
  MORTGAGE_CONSTANTS,
  AFFORDABILITY_CONSTANTS,
  RENT_VS_BUY_CONSTANTS,
  SHARED_CONSTANTS
} from './calculator-constants';

export const calculateMortgagePayment = (loanAmount, interestRate, termYears) => {
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = termYears * 12;

  if (monthlyRate === 0) {
    return loanAmount / numberOfPayments;
  }

  const monthlyPayment =
    (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

  return monthlyPayment;
};

export const calculatePaymentBreakdown = (
  loanAmount,
  interestRate,
  termYears,
  homePrice,
  monthlyTaxes = 0,
  monthlyInsurance = 0
) => {
  const principalAndInterest = calculateMortgagePayment(loanAmount, interestRate, termYears);

  // Calculate LTV (Loan-to-Value) ratio
  const ltv = homePrice > 0 ? loanAmount / homePrice : 0;

  // Calculate PMI based on LTV ratio (matching live site logic)
  let mortgageInsurance = 0;
  if (ltv > 0.9) {
    mortgageInsurance =
      (loanAmount * MORTGAGE_CONSTANTS.PMI_RATE_90_95) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  } else if (ltv > 0.85) {
    mortgageInsurance =
      (loanAmount * MORTGAGE_CONSTANTS.PMI_RATE_85_90) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  } else if (ltv > 0.8) {
    mortgageInsurance =
      (loanAmount * MORTGAGE_CONSTANTS.PMI_RATE_80_85) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  }
  // LTV <= 0.8 means no PMI (already 0)

  const total = principalAndInterest + monthlyTaxes + monthlyInsurance + mortgageInsurance;

  return {
    principalAndInterest: principalAndInterest,
    taxes: monthlyTaxes,
    insurance: monthlyInsurance,
    mortgageInsurance: mortgageInsurance,
    total: total
  };
};

export const calculateAffordability = (
  monthlyIncome,
  monthlyDebts,
  downPayment,
  interestRate,
  termYears,
  targetDebtToIncomeRatio = 0.36
) => {
  const maxDebtToIncomeRatio = AFFORDABILITY_CONSTANTS.MAX_HOUSING_DTI;
  const maxTotalDebtRatio = targetDebtToIncomeRatio;

  const maxHousingPayment = monthlyIncome * maxDebtToIncomeRatio;
  const maxTotalDebt = monthlyIncome * maxTotalDebtRatio;
  const availableForHousing = maxTotalDebt - monthlyDebts;

  // If debts are too high to afford any mortgage, user can only afford down payment
  if (availableForHousing <= 0) {
    const calculatedDTI = monthlyIncome > 0 ? monthlyDebts / monthlyIncome : 0;
    const cappedDTI = Math.min(0.5, calculatedDTI);

    return {
      maxPurchasePrice: downPayment,
      maxLoanAmount: 0,
      maxHousingPayment: 0,
      debtToIncomeRatio: cappedDTI
    };
  }

  const actualMaxHousingPayment = Math.min(maxHousingPayment, availableForHousing);

  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = termYears * 12;

  let maxLoanAmount = 0;
  if (monthlyRate === 0) {
    maxLoanAmount = actualMaxHousingPayment * numberOfPayments;
  } else {
    maxLoanAmount =
      (actualMaxHousingPayment * (Math.pow(1 + monthlyRate, numberOfPayments) - 1)) /
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments));
  }

  // Ensure we don't return negative values - cap at 0
  const maxPurchasePrice = Math.max(0, maxLoanAmount + downPayment);
  const cappedMaxLoanAmount = Math.max(0, maxLoanAmount);
  const cappedMaxHousingPayment = Math.max(0, actualMaxHousingPayment);

  // Calculate DTI and cap at 50% (0.5)
  const calculatedDTI =
    monthlyIncome > 0 ? (cappedMaxHousingPayment + monthlyDebts) / monthlyIncome : 0;
  const cappedDTI = Math.min(0.5, calculatedDTI);

  return {
    maxPurchasePrice,
    maxLoanAmount: cappedMaxLoanAmount,
    maxHousingPayment: cappedMaxHousingPayment,
    debtToIncomeRatio: cappedDTI
  };
};

export const calculateRefinance = (
  currentBalance,
  currentPayment,
  newRate,
  newTerm,
  closingCosts = 0
) => {
  const newPayment = calculateMortgagePayment(currentBalance, newRate, newTerm);

  const monthlySavings = currentPayment - newPayment;
  const breakEvenMonths =
    closingCosts > 0 && monthlySavings > 0 ? closingCosts / monthlySavings : 0;

  return {
    currentPayment,
    newPayment,
    monthlySavings,
    breakEvenMonths: Math.ceil(breakEvenMonths)
  };
};

export const formatCurrency = (value) => {
  return formatMoney(value);
};

export const formatPercentage = (value) => {
  return formatPercent(value);
};

export const formatInteger = (value) => {
  return formatNumber(Math.round(value));
};

export const validatePositiveNumber = (value) => {
  const num = parseFloat(value.toString().replace(/[$,%]/g, ''));
  return !isNaN(num) && num >= 0 ? num : 0;
};

export const validatePercentage = (value) => {
  const num = parseFloat(value.toString().replace(/[%]/g, ''));
  return !isNaN(num) && num >= 0 && num <= 100 ? num : 0;
};

export const formatCurrencyInput = (input, cursorPosition = 0) => {
  if (typeof input !== 'string') {
    input = String(input || '');
  }

  // Remove all non-numeric characters except decimal point
  const numericOnly = input.replace(/[^0-9.]/g, '');

  // Ensure only one decimal point
  const parts = numericOnly.split('.');
  const beforeDecimal = parts[0] || '';
  const afterDecimal = parts.length > 1 ? '.' + parts[1].slice(0, 2) : '';

  const cleanNumeric = beforeDecimal + afterDecimal;

  // Convert to number for validation
  const numericValue = parseFloat(cleanNumeric) || 0;

  // Format with commas
  const integerPart = Math.floor(numericValue).toString();
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Add decimal part if it exists
  const decimalPart = afterDecimal;
  const formattedNumber = formattedInteger + decimalPart;

  // Add dollar sign
  const formattedDisplay = '$' + formattedNumber;

  // Calculate new cursor position
  const originalLength = input.length;
  const newLength = formattedDisplay.length;
  const lengthDifference = newLength - originalLength;
  const newCursorPosition = Math.max(1, cursorPosition + lengthDifference); // Ensure cursor stays after $

  return {
    displayValue: formattedDisplay,
    numericValue: numericValue,
    cursorPosition: newCursorPosition
  };
};

export const formatPercentageInput = (input, cursorPosition = 0) => {
  if (typeof input !== 'string') {
    input = String(input || '');
  }

  // Remove all non-numeric characters except decimal point
  const numericOnly = input.replace(/[^0-9.]/g, '');

  // Ensure only one decimal point and limit to 2 decimal places
  const parts = numericOnly.split('.');
  const beforeDecimal = parts[0] || '';
  const afterDecimal = parts.length > 1 ? '.' + parts[1].slice(0, 2) : '';

  const cleanNumeric = beforeDecimal + afterDecimal;
  const numericValue = Math.min(100, parseFloat(cleanNumeric) || 0);

  // Format the display value
  const formattedNumber = numericValue.toString();
  const formattedDisplay = formattedNumber + '%';

  // Calculate new cursor position
  const originalLength = input.length;
  const newLength = formattedDisplay.length;
  const lengthDifference = newLength - originalLength;
  const newCursorPosition = Math.max(
    0,
    Math.min(formattedNumber.length, cursorPosition + lengthDifference)
  );

  return {
    displayValue: formattedDisplay,
    numericValue: numericValue,
    cursorPosition: newCursorPosition
  };
};

export const extractNumericValue = (formattedValue) => {
  if (typeof formattedValue !== 'string') {
    return 0;
  }

  const numericOnly = formattedValue.replace(/[^0-9.]/g, '');
  return parseFloat(numericOnly) || 0;
};

export const calculateRentVsBuy = (
  purchasePrice,
  downPayment,
  interestRate,
  loanTerm,
  monthlyRent,
  yearsStaying
) => {
  const loanAmount = purchasePrice - downPayment;
  const ltv = purchasePrice > 0 ? loanAmount / purchasePrice : 0;

  // Apply FHA upfront MIP if down payment is less than 5%
  const adjustedLoanAmount =
    ltv > RENT_VS_BUY_CONSTANTS.FHA_THRESHOLD_LTV
      ? loanAmount * (1 + RENT_VS_BUY_CONSTANTS.UPFRONT_MIP_RATE)
      : loanAmount;

  // Calculate base mortgage payment
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = loanTerm * 12;
  const monthlyPI =
    (adjustedLoanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

  const monthlyTaxes =
    (purchasePrice * RENT_VS_BUY_CONSTANTS.PROPERTY_TAX_RATE) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  const monthlyInsurance =
    (purchasePrice * RENT_VS_BUY_CONSTANTS.INSURANCE_RATE) / SHARED_CONSTANTS.MONTHS_PER_YEAR;

  // Calculate PMI based on LTV
  let monthlyPMI = 0;
  if (ltv > 0.9) {
    monthlyPMI =
      (adjustedLoanAmount * MORTGAGE_CONSTANTS.PMI_RATE_90_95) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  } else if (ltv > 0.85) {
    monthlyPMI =
      (adjustedLoanAmount * MORTGAGE_CONSTANTS.PMI_RATE_85_90) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  } else if (ltv > 0.8) {
    monthlyPMI =
      (adjustedLoanAmount * MORTGAGE_CONSTANTS.PMI_RATE_80_85) / SHARED_CONSTANTS.MONTHS_PER_YEAR;
  }

  const totalMonthlyPayment = monthlyPI + monthlyTaxes + monthlyInsurance + monthlyPMI;

  // Key assumptions
  const homeAppreciationRate = RENT_VS_BUY_CONSTANTS.HOME_APPRECIATION_RATE;
  const rentAppreciationRate = RENT_VS_BUY_CONSTANTS.RENT_APPRECIATION_RATE;
  const maintenanceRate = RENT_VS_BUY_CONSTANTS.MAINTENANCE_RATE;
  const sellingCostRate = RENT_VS_BUY_CONSTANTS.SELLING_COST_RATE;
  const investmentReturnRate = RENT_VS_BUY_CONSTANTS.INVESTMENT_RETURN_RATE;

  const monthsStaying = yearsStaying * 12;

  // Calculate total costs over the full period using YEARLY appreciation (matching original)
  let pi2 = 0; // Principal & Interest
  let c2 = 0; // Taxes
  let d2 = 0; // Insurance
  let mi2 = 0; // Mortgage Insurance
  let rent2 = 0; // Rent with 2.5% appreciation
  let rent3 = 0; // Rent with 3.5% appreciation
  let remainingBalance = adjustedLoanAmount;

  // Original code applies appreciation factor to PAYMENTS each year
  for (let year = 0; year < yearsStaying; year++) {
    const b4 = Math.pow(1 + rentAppreciationRate, year); // 3.5% yearly appreciation factor
    const b3 = Math.pow(1 + RENT_VS_BUY_CONSTANTS.RENT_APPRECIATION_RATE_ALT, year); // 2.5% yearly appreciation factor

    // Ownership costs (payments grow with appreciation factor)
    pi2 += monthlyPI * SHARED_CONSTANTS.MONTHS_PER_YEAR * b4;
    c2 += monthlyTaxes * SHARED_CONSTANTS.MONTHS_PER_YEAR * b4;
    d2 += monthlyInsurance * SHARED_CONSTANTS.MONTHS_PER_YEAR * b4;
    mi2 += monthlyPMI * SHARED_CONSTANTS.MONTHS_PER_YEAR * b4;

    // Rental costs (two different appreciation rates like original)
    rent2 +=
      (1 + RENT_VS_BUY_CONSTANTS.RENTERS_INSURANCE_FACTOR) *
      b3 *
      monthlyRent *
      SHARED_CONSTANTS.MONTHS_PER_YEAR;
    rent3 += b4 * monthlyRent * SHARED_CONSTANTS.MONTHS_PER_YEAR;
  }

  const totalOwnershipPayments = pi2 + c2 + d2 + mi2;

  // Calculate remaining balance using original formula (reverse amortization)
  const m2 = 360 - yearsStaying * SHARED_CONSTANTS.MONTHS_PER_YEAR; // Remaining payments
  const z = 1 + interestRate / 1200;
  const r2 = Math.pow(z, m2);
  const h2 = monthlyPI / (((interestRate / 1200) * r2) / (r2 - 1)); // Remaining balance
  remainingBalance = h2;

  // Original formula: o = h2 + w * sellingCostRate - w (this is NEGATIVE when you have equity)
  const finalHomeValue = purchasePrice * Math.pow(1 + homeAppreciationRate, yearsStaying);
  const o = remainingBalance + finalHomeValue * sellingCostRate - finalHomeValue; // Cost after sale (negative = gain)

  // Opportunity cost of down payment (matching original: o2)
  const o2 = downPayment * Math.pow(1 + investmentReturnRate, yearsStaying);

  // Maintenance cost (matching original: q = maintenanceRate * (n+1) * p)
  const q = maintenanceRate * (yearsStaying + 1) * purchasePrice;

  // Lost cost component (matching original: lost = c2 + d2 + mi2 + pi2 - t * 12 * n)
  const lost =
    totalOwnershipPayments - totalMonthlyPayment * SHARED_CONSTANTS.MONTHS_PER_YEAR * yearsStaying;

  // Interest paid over the period (matching original calculation)
  const totalInterest =
    yearsStaying * SHARED_CONSTANTS.MONTHS_PER_YEAR * monthlyPI -
    (adjustedLoanAmount - remainingBalance);

  // TOTAL OWNERSHIP COST (matching original exactly)
  // Original: u = o + q + lost + 0.8*i*12*n + d2 + (t-i-j)*12*n + o2 - taxDeduction*interest
  const totalOwnershipCost =
    o +
    q +
    lost +
    0.8 * monthlyTaxes * SHARED_CONSTANTS.MONTHS_PER_YEAR * yearsStaying +
    d2 +
    (totalMonthlyPayment - monthlyTaxes - monthlyInsurance) *
      SHARED_CONSTANTS.MONTHS_PER_YEAR *
      yearsStaying +
    o2 -
    RENT_VS_BUY_CONSTANTS.MORTGAGE_INTEREST_TAX_DEDUCTION * totalInterest;

  // Rental costs (matching original: u2 = rent2 + lost2 + arent * Math.pow(1+rentRate, n))
  const lost2 = rent3 - yearsStaying * SHARED_CONSTANTS.MONTHS_PER_YEAR * monthlyRent;
  const totalRentalCost =
    rent2 + lost2 + monthlyRent * Math.pow(1 + rentAppreciationRate, yearsStaying);

  // Determine recommendation
  const recommendation = totalOwnershipCost < totalRentalCost ? 'Buy' : 'Rent';
  const avgOwnershipMonthlyCost = totalOwnershipCost / monthsStaying;
  const avgRentalMonthlyCost = totalRentalCost / monthsStaying;
  const monthlySavings = Math.abs(avgOwnershipMonthlyCost - avgRentalMonthlyCost);

  // Calculate break-even: at what month does owning become cheaper?
  let breakEvenMonths = 0;

  for (let month = 1; month <= monthsStaying; month++) {
    const yearsElapsed = month / 12;

    // Ownership cost if you sell at this month
    let totalPayments = 0;
    let maintenanceToDate = 0;
    let balanceAtMonth = adjustedLoanAmount;

    for (let m = 1; m <= month; m++) {
      const yearsElapsedAtM = m / 12;
      const appreciationFactor = Math.pow(1 + homeAppreciationRate, yearsElapsedAtM);

      totalPayments += totalMonthlyPayment;
      maintenanceToDate += (purchasePrice * maintenanceRate * appreciationFactor) / 12;

      const monthlyInterest = balanceAtMonth * monthlyRate;
      const principalPayment = monthlyPI - monthlyInterest;
      balanceAtMonth = Math.max(0, balanceAtMonth - principalPayment);
    }

    // For break-even calculation, match the chart: net position including equity
    const homeValueNow = purchasePrice * Math.pow(1 + homeAppreciationRate, yearsElapsed);
    const sellingCostsNow = homeValueNow * sellingCostRate;
    const proceedsFromSale = homeValueNow - balanceAtMonth - sellingCostsNow;

    const downPaymentGrowth = downPayment * Math.pow(1 + investmentReturnRate, yearsElapsed);
    const opportunityCost = downPaymentGrowth - downPayment;

    const netOwnershipCost =
      downPayment + totalPayments + maintenanceToDate + opportunityCost - proceedsFromSale;

    // Rental cumulative out-of-pocket
    let cumulativeRentalCost = 0;
    for (let m = 1; m <= month; m++) {
      const yearsElapsedAtM = m / 12;
      const rentFactor = Math.pow(1 + rentAppreciationRate, yearsElapsedAtM);
      const adjustedRent = monthlyRent * rentFactor;
      cumulativeRentalCost +=
        adjustedRent + adjustedRent * RENT_VS_BUY_CONSTANTS.RENTERS_INSURANCE_FACTOR;
    }

    const netRentalCost = cumulativeRentalCost - opportunityCost;

    if (breakEvenMonths === 0 && netOwnershipCost < netRentalCost) {
      breakEvenMonths = month;
    }
  }

  return {
    ownershipMonthlyCost: Math.round(avgOwnershipMonthlyCost),
    rentalMonthlyCost: Math.round(avgRentalMonthlyCost),
    monthlySavings: Math.round(monthlySavings),
    recommendation: recommendation,
    breakEvenMonths: breakEvenMonths || monthsStaying,
    monthlyMortgagePayment: totalMonthlyPayment,
    totalOwnershipCost: totalOwnershipCost,
    totalRentalCost: totalRentalCost
  };
};
